import * as OTPAuth from 'otpauth';

import type { TOTPPayload } from '../types';

/**
 * Clean and normalize TOTP secret for storage.
 * - Removes all whitespace (spaces, tabs, newlines)
 * - Removes hyphens and dashes (common in readable formats)
 * - Converts to uppercase (Base32 is case-insensitive)
 * - Strips trailing padding ('=') as otpauth library handles it
 */
export function normalizeTOTPSecret(secret: string): string {
  return secret
    .replace(/[\s\-]/g, '') // Remove whitespace and hyphens
    .toUpperCase() // Base32 is case-insensitive, uppercase is standard
    .replace(/=+$/, ''); // Remove trailing padding (optional in Base32)
}

/**
 * Validate TOTP secret format (Base32).
 * Returns true if the secret can be decoded as valid Base32.
 */
export function isValidTOTPSecret(secret: string): boolean {
  try {
    const normalized = normalizeTOTPSecret(secret);
    if (normalized.length === 0) return false;
    // Validate Base32 charset: A-Z and 2-7 only
    if (!/^[A-Z2-7]+$/.test(normalized)) return false;
    // Try to decode it
    OTPAuth.Secret.fromBase32(normalized);
    return true;
  } catch {
    return false;
  }
}

/**
 * Generate TOTP code from payload.
 * Uses RFC 6238 compliant generation via otpauth library.
 *
 * @param payload - TOTP payload with secret and optional config
 * @param timestampMs - Optional timestamp for testing (defaults to Date.now())
 * @returns 6-8 digit TOTP code as string (zero-padded)
 */
export function generateTOTPCode(payload: TOTPPayload, timestampMs?: number): string {
  const algorithm = payload.algorithm || 'SHA1';
  const digits = payload.digits || 6;
  const period = payload.period || 30;

  // Normalize secret before decoding (in case it wasn't normalized on storage)
  const normalizedSecret = normalizeTOTPSecret(payload.secret);

  const totp = new OTPAuth.TOTP({
    issuer: payload.title,
    label: payload.title,
    algorithm,
    digits,
    period,
    secret: OTPAuth.Secret.fromBase32(normalizedSecret),
  });

  // Use provided timestamp or current time
  const timestamp = timestampMs !== undefined ? timestampMs : Date.now();

  return totp.generate({ timestamp });
}

/**
 * Get remaining seconds for current TOTP period.
 *
 * @param period - TOTP period in seconds (default 30)
 * @param timestampMs - Optional timestamp for testing (defaults to Date.now())
 * @returns Seconds remaining until next code
 */
export function getTOTPRemainingSeconds(period: number = 30, timestampMs?: number): number {
  const now = Math.floor((timestampMs !== undefined ? timestampMs : Date.now()) / 1000);
  return period - (now % period);
}

/**
 * Parse otpauth:// URI from QR code.
 * Handles standard TOTP URIs per Google Authenticator Key URI Format.
 *
 * @param uri - otpauth:// URI string
 * @returns Parsed TOTP payload or null if invalid
 */
export function parseOTPAuthURI(uri: string): TOTPPayload | null {
  try {
    const parsed = OTPAuth.URI.parse(uri);

    if (!(parsed instanceof OTPAuth.TOTP)) {
      return null;
    }

    // Normalize the secret from the parsed URI
    const secret = normalizeTOTPSecret(parsed.secret.base32);

    // Extract algorithm, handling case-insensitive comparison
    let algorithm: 'SHA1' | 'SHA256' | 'SHA512' = 'SHA1';
    const parsedAlgo = parsed.algorithm?.toUpperCase();
    if (parsedAlgo === 'SHA256') algorithm = 'SHA256';
    else if (parsedAlgo === 'SHA512') algorithm = 'SHA512';

    return {
      type: 'totp',
      title: parsed.issuer || parsed.label || 'Unknown',
      secret,
      algorithm,
      digits: parsed.digits || 6,
      period: parsed.period || 30,
    };
  } catch {
    return null;
  }
}

/**
 * Generate otpauth:// URI for a TOTP payload.
 * Can be used to generate QR codes or export tokens.
 *
 * @param payload - TOTP payload
 * @returns otpauth:// URI string
 */
export function generateOTPAuthURI(payload: TOTPPayload): string {
  const normalizedSecret = normalizeTOTPSecret(payload.secret);

  const totp = new OTPAuth.TOTP({
    issuer: payload.title,
    label: payload.title,
    algorithm: payload.algorithm || 'SHA1',
    digits: payload.digits || 6,
    period: payload.period || 30,
    secret: OTPAuth.Secret.fromBase32(normalizedSecret),
  });

  return totp.toString();
}
